import { type ApolloClient, gql } from "@apollo/client";
import {
  type AtomData,
  type AtomDescriptor,
  type AtomInfos,
  type CONTEXT_KEY,
  type ContextValues,
  type EntityId,
  type EntityInfos,
  type FindParameters,
  type FindResult,
  type GetAtomParameters,
  type ITransaction,
  isAtomPatchesPacket,
  type SubscribeAtomPacket,
} from "@atoms/atom-types";
import { EventEmitter } from "eventemitter3";
import type { Delta } from "jsondiffpatch";
import { diff, patch } from "jsondiffpatch";
import cloneDeep from "lodash/cloneDeep";
import {
  type SubscriptionCallback,
  type SubscriptionCallbacks,
  subscriptionDocument,
} from "./AtomStoreProvider";
import { useUserStore } from "./hooks/useMyUserId";
import { formatUnknownError } from "./notifications/formatError";
import { getGlobalNotifier } from "./notifications/notifier";
import type { SelectorFunction } from "./use-atom/makeUseAtom";

/**
 * Extended transaction interface for frontend that includes findAtoms
 */
export interface IFrontendTransaction extends ITransaction {
  findAtoms<
    T extends AtomData,
    P extends FindParameters<T> = FindParameters<T>,
  >(
    atomType: string,
    parameters: P,
    overrides?: ContextValues,
  ): Promise<FindResult<T, P> | null>;
}

export class HashValidationError extends Error {
  constructor(
    public hashFromBackend: string,
    public localHash: string,
    public atomType: string,
    public context: ContextValues,
  ) {
    super(
      `Hash validation failed for atom ${atomType} with context ${JSON.stringify(
        context,
      )}. Hash from backend: ${hashFromBackend}, local hash: ${localHash}`,
    );
  }
}
// async function sha256(message: string): Promise<string> {
//   // encode as UTF-8
//   const msgBuffer = new TextEncoder().encode(message);

//   // hash the message
//   const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

//   // convert ArrayBuffer to Array
//   const hashArray = Array.from(new Uint8Array(hashBuffer));

//   // convert bytes to hex string
//   const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
//   return hashHex;
// }
export class AtomStore<AtomType extends string = string> extends EventEmitter {
  private subscriptions: SubscriptionCallbacks = {};

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  private state: { [key in AtomType]?: any } = {};

  private retries: {
    [path: string]: { count: number; timeout?: ReturnType<typeof setTimeout> };
  } = {};

  // Track all subscribe events
  private subscriptionEvents: Array<{
    atomType: AtomType;
    context: ContextValues;
    parameters?: GetAtomParameters;
    timestamp: number;
  }> = [];

  constructor(
    private apolloClient: ApolloClient<object>,
    public readonly atomInfosExtended: AtomInfos<string, string>,
    public readonly entityInfos: EntityInfos<string, CONTEXT_KEY<string>>,
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    public onError: (error: any) => void = (error) => {
      const formatted = formatUnknownError(error, {
        defaultMessage: "Request failed",
        keyPrefix: "api",
      });
      // Use a stable key when possible so repeated failures don't spam the user.
      const key = formatted.key ?? `api:${formatted.message}`;
      getGlobalNotifier().notify(formatted.level, formatted.message, {
        key,
        description: formatted.description,
      });
    },
  ) {
    super();
  }

  public getTransaction(context: ContextValues = {}): IFrontendTransaction {
    const aquireAtomValue = async <T extends AtomData>(
      atomType: AtomType,
      overrides?: ContextValues,
      parameters?: GetAtomParameters,
    ): Promise<T> => {
      const combinedContext = {
        ...context,
        ...overrides,
      };
      const existingValue = this.getAtom(atomType, combinedContext, parameters);
      if (existingValue) {
        return Promise.resolve(existingValue);
      }
      return new Promise((resolve, reject) => {
        const callback = async (value: T | null) => {
          if (!value) {
            reject(new Error(`Atom ${atomType} not found`));
            return;
          }
          await this.unsubscribe(
            atomType,
            combinedContext,
            callback,
            parameters,
          );
          resolve(value);
        };
        try {
          this.subscribe(
            atomType,
            combinedContext,
            callback,
            undefined,
            parameters,
          );
        } catch (e) {
          reject(e);
        }
      });
    };

    const findAtoms = async <
      T extends AtomData,
      P extends FindParameters<T> = FindParameters<T>,
    >(
      atomType: string,
      parameters: P,
      overrides?: ContextValues,
    ): Promise<FindResult<T, P> | null> => {
      return this.executeFindAtoms<T, P>(
        atomType,
        parameters,
        context,
        overrides,
      );
    };

    return {
      getEntry: <K extends CONTEXT_KEY<string>>(
        key: K,
      ): EntityId<K> | undefined => context[key] as EntityId<K> | undefined,
      getActingUserOrThrow: () => {
        const userId = useUserStore.getState().userId;
        if (!userId) {
          throw new Error("No user ID found");
        }
        return userId;
      },
      aquireAtomValue,
      findAtoms,
    };
  }

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  public updateErrorCallback(callback: (error: any) => void): void {
    this.onError = callback;
  }

  public getApolloClient(): ApolloClient<object> {
    return this.apolloClient;
  }

  /**
   * Execute a findAtoms query against the GraphQL backend
   * @param atomType The atom type to query
   * @param parameters Find parameters (filters, pagination, etc.)
   * @param context Context values for the query
   * @param overrides Optional context overrides
   * @returns Find result or null if no atoms found
   */
  public async executeFindAtoms<
    T extends AtomData,
    P extends FindParameters<T> = FindParameters<T>,
  >(
    atomType: string,
    parameters: P,
    context: ContextValues,
    overrides?: ContextValues,
  ): Promise<FindResult<T, P> | null> {
    const combinedContext = {
      ...context,
      ...overrides,
    };

    try {
      const variables = {
        context: combinedContext,
        atomType,
        parameters: parameters,
        overrides: overrides || {},
      };

      const { data: responseData } = await this.apolloClient.query<{
        findAtoms: FindResult<T, P>;
      }>({
        query: gql`
          query findAtoms(
            $context: JSON!
            $atomType: String!
            $parameters: JSON!
            $overrides: JSON
          ) {
            findAtoms(
              context: $context
              atomType: $atomType
              parameters: $parameters
              overrides: $overrides
            )
          }
        `,
        fetchPolicy: "no-cache",
        errorPolicy: "all",
        variables,
      });

      return responseData?.findAtoms ?? null;
    } catch (error) {
      console.error("Error in executeFindAtoms:", error);
      throw error;
    }
  }

  public getPresentAtoms(): AtomDescriptor[] {
    return Object.keys(this.subscriptions) as AtomDescriptor[];
  }

  private formatParameters(parameters?: GetAtomParameters): string {
    if (!parameters || Object.keys(parameters).length === 0) {
      return "";
    }
    const paramString = Object.entries(parameters)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${JSON.stringify(value)}`)
      .join(",");
    return `?${paramString}`;
  }

  private notifyAllCallbacks<RType extends object>(
    atomId: string,
    callbacks: Array<SubscriptionCallback<RType>>,
    value: RType | null,
    patches: Delta | undefined,
    initial?: boolean,
  ): void {
    for (const cb of [...callbacks]) {
      try {
        cb(value, patches, initial);
      } catch (e) {
        console.error(`Error in callback for ${atomId}:`, e);
      }
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
  }

  private isDoesNotExistPacket(value: unknown): value is {
    __doesNotExist: unknown;
  } {
    return this.isRecord(value) && "__doesNotExist" in value;
  }

  private isDeltaPacket(value: unknown): value is { __delta: Delta } {
    if (!this.isRecord(value)) return false;
    if (!("__delta" in value)) return false;
    const maybeDelta = (value as { __delta: unknown }).__delta;
    return typeof maybeDelta !== "undefined";
  }

  private isFullValuePacket<RType extends object>(
    value: unknown,
  ): value is { __value: RType } {
    return this.isRecord(value) && "__value" in value;
  }

  private isErrorPacket(value: unknown): value is { error: string } {
    return (
      this.isRecord(value) &&
      "error" in value &&
      typeof value.error === "string"
    );
  }

  private handleDoesNotExist<RType extends object>(atomId: string): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    sub.lastValue = null;
    this.notifyAllCallbacks<RType>(
      atomId,
      sub.callbacks as Array<SubscriptionCallback<RType>>,
      null,
      {},
    );
  }

  private handleErrorPacket(atomId: string, errorMessage: string): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    const error = new Error(errorMessage);
    sub.error = error;
    sub.lastValue = null;
    // Notify callbacks with null value so they know subscription failed
    // The error is stored in sub.error for hooks that check it
    this.notifyAllCallbacks<object>(
      atomId,
      sub.callbacks as Array<SubscriptionCallback<object>>,
      null,
      {},
    );
  }

  private handleDeltaPacket<RType extends object>(
    atomId: string,
    type: AtomType,
    __delta: Delta,
  ): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    if (!__delta || Object.keys(__delta).length === 0) return;
    const newValueRaw = cloneDeep(sub.lastValue ?? {}) as RType;
    patch(newValueRaw, __delta);
    sub.lastValue = newValueRaw as object;
    // biome-ignore lint/suspicious/noExplicitAny: legacy state shape
    (this.state as any)[type] = newValueRaw as unknown as object;
    this.notifyAllCallbacks<RType>(
      atomId,
      sub.callbacks as Array<SubscriptionCallback<RType>>,
      newValueRaw,
      __delta,
    );
  }

  private handleFullValue<RType extends object>(
    atomId: string,
    type: AtomType,
    value: RType,
  ): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    sub.lastValue = value as object;
    // biome-ignore lint/suspicious/noExplicitAny: legacy state shape
    (this.state as any)[type] = value as unknown as object;
    const patchesForCallback = diff({}, value);
    this.notifyAllCallbacks<RType>(
      atomId,
      sub.callbacks as Array<SubscriptionCallback<RType>>,
      value,
      (patchesForCallback || undefined) as Delta | undefined,
      true,
    );
  }

  private handleComplete(atomId: string): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    sub.completed = true;
    // Do not coerce lastValue to null here:
    // - `null` is reserved for the stable "does not exist" state [[memory:12469949]]
    // - subscription completion can happen during transient disconnects/reconnects.
    // We keep the last known value so the UI can continue rendering stale data,
    // while the connection layer handles resubscribe/refetch on reconnect.
  }

  private handleGraphQLErrors<RType extends object>(
    atomId: string,
    graphQLErrors?: Array<Error>,
  ): void {
    if (!graphQLErrors) return;
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    for (const error of graphQLErrors) {
      sub.error = error;
      // Don't pass error as data - pass null to indicate failure
      // The error is stored in sub.error for hooks that check it
      this.notifyAllCallbacks<RType>(
        atomId,
        sub.callbacks as Array<SubscriptionCallback<RType>>,
        null,
        {},
      );
    }
  }

  public pathForAtomWithContext(
    type: AtomType,
    context: ContextValues,
    parameters?: GetAtomParameters,
  ): string {
    const entityType = this.atomInfosExtended[type]?.entityType;
    if (!entityType) {
      throw new Error(`No entity type mapping for atom ${type}`);
    }

    const entityInfo = this.entityInfos[entityType];
    if (!entityInfo) {
      throw new Error(`No entity info for entity type ${entityType}`);
    }

    for (const path of entityInfo.validContexts) {
      const contextRecord = context as Record<string, string | undefined>;
      const x = path.map((p) => {
        if (p === null) {
          return undefined;
        }
        return contextRecord[p];
      });
      if (x.every((v) => v !== undefined)) {
        let atomPath = `${path
          .map((p) => {
            if (p === null) {
              return "";
            }
            return `/${p.replace(/<|>/g, "")}:${contextRecord[p]}`;
          })
          .join("")}/${type}`;

        // Include parameters in the path if they exist
        const paramsSuffix = this.formatParameters(parameters);
        if (paramsSuffix) atomPath += paramsSuffix;

        return atomPath;
      }
    }
    console.debug(
      "Atom:",
      type,
      "Given context:",
      Object.entries(context)
        .filter(([_k, v]) => v !== undefined)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", "),
      "Required:",
      entityInfo.validContexts.map((p) => p.join(", ")).join(" OR "),
    );
    throw new Error(`No valid context for atom ${type}`);
  }

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  public getAtom<RType = any>(
    type: AtomType,
    context: ContextValues,
    parameters?: GetAtomParameters,
  ): RType | null | undefined {
    const atomId = this.pathForAtomWithContext(type, context, parameters);

    if (this.subscriptions[atomId]?.error) {
      throw this.subscriptions[atomId]?.error;
    }

    const lastValue = this.subscriptions[atomId]?.lastValue;
    // Important: `null` means "atom does not exist" (intentional, stable state),
    // while `undefined` means "not yet received".
    if (typeof lastValue === "undefined") return undefined;
    return lastValue as RType | null;
  }

  public subscribe<RType extends object = object>(
    type: AtomType,
    context: ContextValues,
    callback: SubscriptionCallback<RType>,
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    selector: SelectorFunction<RType, any> = (value): RType => value,
    parameters?: GetAtomParameters,
  ): void {
    const atomId = this.pathForAtomWithContext(type, context, parameters);
    const matchingSubscription = this.subscriptions[atomId];
    if (!matchingSubscription) {
      if (this.retries[atomId]) {
        clearTimeout(this.retries[atomId]?.timeout);
        delete this.retries[atomId];
      }

      this.retries[atomId] = { count: 0 };

      const getSubscription = (): Promise<ZenObservable.Subscription> =>
        this.startSubscription<RType>(atomId, type, context, parameters);
      this.subscriptions[atomId] = {
        selectors: [selector],
        callbacks: [callback as SubscriptionCallback<object>],
        lastValue: undefined,
        current: getSubscription(),
        atomType: type,
        context: context as Record<string, unknown>,
        parameters: parameters as Record<string, unknown> | undefined,
      };
      // Track the subscribe event
      this.subscriptionEvents.push({
        atomType: type,
        context,
        parameters,
        timestamp: Date.now(),
      });
    } else {
      if (this.subscriptions[atomId]?.lastValue !== undefined) {
        const rawLastValue = this.subscriptions[atomId]
          ?.lastValue as RType | null;
        const valueForCallback = rawLastValue;
        const patchesForCallback = diff({}, valueForCallback);
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        callback(valueForCallback, (patchesForCallback || []) as any, true);
      }
      this.subscriptions[atomId]?.callbacks.push(
        callback as SubscriptionCallback<object>,
      );
      this.subscriptions[atomId]?.selectors.push(selector);
    }
    this.addListener(atomId, callback);
  }

  private startSubscription<RType extends object = object>(
    atomId: string,
    type: AtomType,
    context: ContextValues,
    parameters?: GetAtomParameters,
  ): Promise<ZenObservable.Subscription> {
    const self = this;
    return new Promise<ZenObservable.Subscription>((resolve) => {
      resolve(
        this.apolloClient
          .subscribe<{
            subscribeAtom: SubscribeAtomPacket<RType, AtomType>;
          }>({
            query: subscriptionDocument,
            fetchPolicy: "no-cache",
            variables: { type, context, parameters },
          })
          .subscribe({
            next: ({ data }) => {
              if (!data) return;
              const maybe = data.subscribeAtom as unknown;
              if (self.isDoesNotExistPacket(maybe)) {
                self.handleDoesNotExist<RType>(atomId);
              } else if (
                isAtomPatchesPacket(
                  maybe as SubscribeAtomPacket<object, AtomType>,
                )
              ) {
                // this has been obsoleted by the delta packet, but there still exists a unfinished partial update functionality in the atom.resolver
                throw new Error("partial updates not implemented");
              } else if (self.isDeltaPacket(maybe)) {
                const { __delta } = maybe;
                self.handleDeltaPacket<RType>(atomId, type, __delta);
              } else if (self.isFullValuePacket<RType>(maybe)) {
                self.handleFullValue<RType>(atomId, type, maybe.__value);
              } else if (self.isErrorPacket(maybe)) {
                self.handleErrorPacket(atomId, maybe.error);
              } else {
                const newValueRaw = data.subscribeAtom as RType; // backward compatibility
                self.handleFullValue<RType>(atomId, type, newValueRaw);
              }
            },
            complete: () => {
              self.handleComplete(atomId);
            },
            error({ graphQLErrors }: { graphQLErrors?: Array<Error> }) {
              self.handleGraphQLErrors<RType>(atomId, graphQLErrors);
            },
          }),
      );
    });
  }

  /**
   * Reopen all active atom subscriptions.
   * Intended to be called after a WebSocket reconnect to avoid lost updates.
   */
  public async resubscribeAll(): Promise<void> {
    const entries = Object.entries(this.subscriptions);
    await Promise.all(
      entries.map(async ([atomId, sub]) => {
        // Only resubscribe if something still listens.
        if (this.listenerCount(atomId) === 0) return;

        if (sub.unsubscribeTimeout) {
          clearTimeout(sub.unsubscribeTimeout);
          sub.unsubscribeTimeout = undefined;
        }

        let current: ZenObservable.Subscription | undefined;
        try {
          current = await sub.current;
        } catch {
          current = undefined;
        }

        const isClosed = !current || current.closed;
        const shouldReopen =
          isClosed || Boolean(sub.completed) || Boolean(sub.error);
        if (!shouldReopen) return;

        if (!sub.atomType || !sub.context) return;

        // Reset transient flags; preserve lastValue (stale is fine until refreshed).
        sub.completed = false;
        sub.error = undefined;
        sub.current = this.startSubscription(
          atomId,
          sub.atomType as AtomType,
          sub.context as ContextValues,
          sub.parameters as GetAtomParameters | undefined,
        );
      }),
    );
  }

  public getEntityType(type: AtomType): string | undefined {
    return this.atomInfosExtended[type]?.entityType;
  }

  public getDefaultValues(type: AtomType): Record<string, unknown> | undefined {
    return this.atomInfosExtended[type]?.defaultValues;
  }

  public hasPermission(_permission: string, _context: ContextValues): boolean {
    return true;
  }

  public async unsubscribe(
    type: AtomType,
    context: ContextValues,
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    callback: SubscriptionCallback<any>,
    parameters?: GetAtomParameters,
  ): Promise<void> {
    const atomId = this.pathForAtomWithContext(type, context, parameters);
    this.removeListener(atomId, callback);

    if (!this.subscriptions[atomId]) {
      console.debug(`No subscription for ${atomId}`);
      return;
    }

    const index = this.subscriptions[atomId]?.callbacks.indexOf(callback);
    if (index === -1) {
      console.debug(`Callback not found for ${atomId}`);
      return;
    }
    this.subscriptions[atomId]?.callbacks.splice(index, 1);
    this.subscriptions[atomId]?.selectors.splice(index, 1);

    const sub = await this.subscriptions[atomId]?.current;
    if (!sub?.closed && this.listenerCount(atomId) === 0) {
      this.subscriptions[atomId].unsubscribeTimeout = setTimeout(() => {
        if (!sub?.closed && this.listenerCount(atomId) === 0) {
          sub?.unsubscribe();
          delete this.subscriptions[atomId];
          if (this.retries[atomId]?.timeout) {
            clearTimeout(this.retries[atomId]?.timeout);
          }
          delete this.retries[atomId];
        }
      }, 1000);
    }
  }

  public getSubscriptionEvents() {
    return this.subscriptionEvents;
  }
}
